import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { notificationsApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const { isAuthenticated } = useAuth();
  const { on, off } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const data = await notificationsApi.getMy();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Real-time listener
  useEffect(() => {
    if (!on || !off) return;
    const handler = (data) => {
      setNotifications(prev => [{ ...data, isRead: false }, ...prev]);
    };
    on('notification', handler);
    return () => off('notification', handler);
  }, [on, off]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch (err) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) { /* ignore */ }
  };

  const filtered = filter === 'unread' ? notifications.filter(n => !n.isRead) : notifications;
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 172800000) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'success': return <span className="np-type-icon np-type-success">✓</span>;
      case 'warning': return <span className="np-type-icon np-type-warning">⚠</span>;
      default: return <span className="np-type-icon np-type-info">ℹ</span>;
    }
  };

  return (
    <div className="notifications-page">
      <Header />
      <div className="np-main">
        <div className="np-container">
          <div className="np-header">
            <div>
              <h1 className="np-title">Notifications</h1>
              <p className="np-subtitle">{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}</p>
            </div>
            <div className="np-header-actions">
              {unreadCount > 0 && (
                <button className="np-mark-all-btn" onClick={handleMarkAllRead}>Mark all as read</button>
              )}
            </div>
          </div>

          <div className="np-filters">
            <button className={`np-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
              All ({notifications.length})
            </button>
            <button className={`np-filter-btn ${filter === 'unread' ? 'active' : ''}`} onClick={() => setFilter('unread')}>
              Unread ({unreadCount})
            </button>
          </div>

          <div className="np-list">
            {loading ? (
              <div className="np-loading">Loading notifications...</div>
            ) : filtered.length === 0 ? (
              <div className="np-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <p>{filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}</p>
              </div>
            ) : (
              filtered.map(n => (
                <div key={n.id} className={`np-item ${!n.isRead ? 'np-item-unread' : ''}`}>
                  <div className="np-item-icon">{typeIcon(n.type)}</div>
                  <div className="np-item-body">
                    <div className="np-item-top">
                      <h3 className="np-item-title">{n.title}</h3>
                      <span className="np-item-time">{formatDate(n.createdAt)}</span>
                    </div>
                    <p className="np-item-message">{n.message}</p>
                  </div>
                  <div className="np-item-actions">
                    {!n.isRead ? (
                      <button className="np-read-btn" onClick={() => handleMarkAsRead(n.id)} title="Mark as read">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                      </button>
                    ) : (
                      <span className="np-read-check" title="Read">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NotificationsPage;
